import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TripData } from 'src/app/shared/model/tripData';
import {
  Contribution,
  PaginatedResponse,
} from 'src/app/shared/models/contribution.models';
import { CountryService } from 'src/app/trip-calculator/services/country.service';
import {
  TripForSelection,
  ContributionStats,
} from './select-trip-modal.models';

@Injectable({
  providedIn: 'root',
})
export class SelectTripModalService {
  private http = inject(HttpClient);
  private countryService = inject(CountryService);

  // State signals
  visible = signal(false);
  loading = signal(false);
  searchTerm = signal('');
  tripsWithStatus = signal<TripForSelection[]>([]);

  // Computed: filtered trips by search term
  filteredTrips = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const trips = this.tripsWithStatus();
    if (!term) return trips;
    return trips.filter(
      (t) =>
        t.title.toLowerCase().includes(term) ||
        t.countryName?.toLowerCase().includes(term),
    );
  });

  // Computed: trips ready to contribute (has impact, not draft)
  readyTrips = computed(() =>
    this.filteredTrips().filter((t) => t.hasImpact && !t.isDraft),
  );

  // Computed: draft trips (no impact or draft status)
  draftTrips = computed(() =>
    this.filteredTrips().filter((t) => t.isDraft || !t.hasImpact),
  );

  // Computed: stats for info card
  contributionStats = computed<ContributionStats>(() => {
    const trips = this.tripsWithStatus();
    const readyTrips = trips.filter((t) => t.hasImpact && !t.isDraft);
    const contributed = readyTrips.filter(
      (t) => t.contributionCount > 0,
    ).length;
    const total = readyTrips.length;
    const drafts = trips.filter((t) => t.isDraft || !t.hasImpact).length;
    return { contributed, total, drafts };
  });

  open(): void {
    this.visible.set(true);
    this.searchTerm.set('');
    this.loadTrips();
  }

  close(): void {
    this.visible.set(false);
  }

  loadTrips(): void {
    this.loading.set(true);

    const tripsUrl = `${environment.apiUrl}/trips`;
    // Use max allowed limit (100) per API validation
    const contributionsUrl = `${environment.apiUrl}/contributions?scope=company&limit=100`;

    forkJoin({
      // No createdVia: the API then only returns the trips created in this
      // app ("screen"). Newest first, as the API defaults to oldest first.
      trips: this.http.get<{ tripData: TripData[] }>(tripsUrl, {
        params: { orderByDate: 'desc' },
      }),
      contributions: this.http.get<PaginatedResponse<Contribution>>(
        contributionsUrl,
      ),
    }).subscribe({
      next: ({ trips, contributions }) => {
        const tripsWithStatus = this.mergeData(
          trips.tripData,
          contributions.data,
        );
        this.tripsWithStatus.set(tripsWithStatus);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading trips for selection:', error);
        this.loading.set(false);
      },
    });
  }

  private mergeData(
    trips: TripData[],
    contributions: Contribution[],
  ): TripForSelection[] {
    // Create a map of tripId -> contribution info
    const contributionsByTrip = new Map<
      string,
      { count: number; lastDate?: Date }
    >();

    contributions.forEach((c) => {
      if (c.trip?.id) {
        const existing = contributionsByTrip.get(c.trip.id);
        const contributionDate = c.createdAt ? new Date(c.createdAt) : undefined;

        if (existing) {
          existing.count++;
          if (
            contributionDate &&
            (!existing.lastDate || contributionDate > existing.lastDate)
          ) {
            existing.lastDate = contributionDate;
          }
        } else {
          contributionsByTrip.set(c.trip.id, {
            count: 1,
            lastDate: contributionDate,
          });
        }
      }
    });

    // Map trips to TripForSelection
    return trips
      .map((trip) => {
        const contributionInfo = contributionsByTrip.get(trip.id ?? '') ?? {
          count: 0,
          lastDate: undefined,
        };

        return {
          id: trip.id ?? '',
          title: trip.title ?? 'Sans titre',
          countryName: trip.country
            ? this.countryService.getCountryLabelByLang(trip.country)
            : undefined,
          imagePath: trip.imagePath,
          days: trip.days ?? 0,
          travellers: trip.travellers ?? 0,
          totalImpact: trip.tripImpact?.totalImpact ?? 0,
          hasImpact: trip.tripImpact !== null && trip.tripImpact !== undefined,
          isDraft: trip.status === 'draft',
          lastContributionDate: contributionInfo.lastDate,
          contributionCount: contributionInfo.count,
        };
      })
      .sort((a, b) => {
        // Sort: ready trips first, then by most recent
        if (a.hasImpact && !a.isDraft && (!b.hasImpact || b.isDraft)) return -1;
        if (b.hasImpact && !b.isDraft && (!a.hasImpact || a.isDraft)) return 1;
        // Within same category, sort by contribution count (not contributed first)
        if (a.contributionCount === 0 && b.contributionCount > 0) return -1;
        if (b.contributionCount === 0 && a.contributionCount > 0) return 1;
        return 0;
      });
  }
}
